/**
 * Erst-Admin-Claim vor dem Login.
 * Vertrag: scripts/edge/edge-claim-redeem.sh und scripts/edge/edge-claim-reissue.sh
 * (--reason rerequest). Passwort nur im JSON-Body, mindestens 12 Zeichen, fail-closed.
 * Kein PocketBase-Admin, kein Support-Versand, kein Code in der Adresse des Aufrufs.
 */

export const CLAIM_PASSWORD_MIN = 12;
export const CLAIM_PASSWORD_MAX = 256;

/** Same-Origin-Pfad des bestehenden Einlöse-Vertrags. */
export const PORTAL_CLAIM_REDEEM_PATH = '/api/edge-claim/redeem';

/** Same-Origin-Pfad der bestehenden erneuten Anforderung (nur rerequest). */
export const PORTAL_CLAIM_REREQUEST_PATH = '/api/edge-claim/rerequest';

const TENANT_RE = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;
const CODE_RE = /^[A-Za-z0-9]{16,128}$/;

export const PORTAL_CLAIM_TEXT = {
  title: 'Zugang einrichten',
  lead:
    'Ein einmaliger Link legt das Passwort auf dem bereits angelegten Konto fest. Der Link gilt 24 Stunden und ist danach verbraucht.',
  kanzlei: 'Kanzlei',
  password: 'Neues Passwort',
  confirm: 'Passwort wiederholen',
  hint: 'Mindestens 12 Zeichen.',
  submit: 'Einlösen',
  rerequest: 'Link erneut anfordern',
  toLogin: 'Zur Anmeldung',
  showPassword: 'Passwort anzeigen',
  hidePassword: 'Passwort verbergen',
  too_short: 'Das Passwort ist zu kurz. Mindestens 12 Zeichen.',
  mismatch: 'Die beiden Eingaben stimmen nicht überein.',
  incomplete: 'Der Link ist unvollständig.',
  used: 'Dieser Link wurde bereits verwendet.',
  expired: 'Dieser Link ist abgelaufen.',
  invalid: 'Dieser Link ist ungültig.',
  unavailable: 'Einlösen ist derzeit nicht möglich.',
  success:
    'Das Passwort ist gesetzt. Der Link ist verbraucht. Die Anmeldung erfolgt auf der Login-Seite.',
  rerequestDone:
    'Ein neuer Link wurde an dieselbe Adresse gesendet. Der bisherige Code gilt nicht mehr.',
  rerequestFail: 'Ein neuer Link kann derzeit nicht angefordert werden.',
} as const;

export type ClaimNotice = keyof typeof PORTAL_CLAIM_TEXT;

export type ClaimLink =
  | { ok: true; tenant: string; code: string }
  | { ok: false; reason: 'incomplete' };

export type ClaimCallStatus =
  | 'success'
  | 'too_short'
  | 'mismatch'
  | 'used'
  | 'expired'
  | 'invalid'
  | 'incomplete'
  | 'unavailable';

export interface ClaimCallResult {
  status: ClaimCallStatus;
}

export function isPortalClaimPath(pathname: string): boolean {
  const path = (pathname || '/').replace(/\/+$/, '') || '/';
  return path === '/claim';
}

export function parseClaimSearch(search: string): ClaimLink {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const tenant = (params.get('tenant') || '').trim();
  const code = (params.get('code') || '').trim();
  if (!TENANT_RE.test(tenant) || !CODE_RE.test(code)) {
    return { ok: false, reason: 'incomplete' };
  }
  return { ok: true, tenant, code };
}

type ClaimResponse = {
  status: number;
  headers: { get(name: string): string | null };
  text(): Promise<string>;
};

type ClaimFetch = (url: string, init: RequestInit) => Promise<ClaimResponse>;

function isHtmlBody(contentType: string, body: string): boolean {
  if (/text\/html/i.test(contentType)) return true;
  const trimmed = body.trimStart();
  return /^<!doctype html/i.test(trimmed) || /^<html/i.test(trimmed);
}

function readReason(body: string): { ok: boolean; reason: string } {
  const trimmed = body.trim();
  if (!trimmed.startsWith('{')) return { ok: false, reason: '' };
  try {
    const data = JSON.parse(trimmed) as { ok?: unknown; reason?: unknown };
    return {
      ok: data.ok === true,
      reason: typeof data.reason === 'string' ? data.reason.trim().toLowerCase() : '',
    };
  } catch {
    return { ok: false, reason: '' };
  }
}

function failureFromBody(body: string, reason: string): ClaimCallStatus | null {
  if (reason === 'used' || /bereits verwendet/i.test(body)) return 'used';
  if (reason === 'expired' || /abgelaufen/i.test(body)) return 'expired';
  if (reason === 'invalid' || /ungueltig|ungültig/i.test(body)) return 'invalid';
  if (reason === 'too_short' || /zu kurz/i.test(body)) return 'too_short';
  return null;
}

/** Antwort des Einlöse-Vertrags. HTML (SPA-Fallback) ist nie Erfolg. */
export function classifyClaimHttp(status: number, contentType: string, body: string): ClaimCallResult {
  if (isHtmlBody(contentType, body)) return { status: 'unavailable' };
  const parsed = readReason(body);
  const failure = failureFromBody(body, parsed.reason);
  if (failure) return { status: failure };
  if (status >= 200 && status < 300 && (parsed.ok || /eingeloest/i.test(body))) {
    return { status: 'success' };
  }
  return { status: 'unavailable' };
}

/** Antwort der erneuten Anforderung. Support-Versand bleibt außerhalb dieser Seite. */
export function classifyClaimRerequestHttp(
  status: number,
  contentType: string,
  body: string,
): ClaimCallResult {
  if (isHtmlBody(contentType, body)) return { status: 'unavailable' };
  const parsed = readReason(body);
  const failure = failureFromBody(body, parsed.reason);
  if (failure) return { status: failure };
  if (status >= 200 && status < 300 && (parsed.ok || /\[claim-reissue\] OK/i.test(body))) {
    return { status: 'success' };
  }
  return { status: 'unavailable' };
}

function jsonInit(body: Record<string, string>): RequestInit {
  return {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  };
}

export async function redeemPortalClaim(
  input: { tenant: string; code: string; password: string; confirm: string },
  fetchImpl: ClaimFetch = fetch,
): Promise<ClaimCallResult> {
  if (!TENANT_RE.test(input.tenant) || !CODE_RE.test(input.code)) {
    return { status: 'incomplete' };
  }
  if (input.password.length < CLAIM_PASSWORD_MIN) {
    return { status: 'too_short' };
  }
  if (input.password.length > CLAIM_PASSWORD_MAX) {
    return { status: 'unavailable' };
  }
  if (input.password !== input.confirm) {
    return { status: 'mismatch' };
  }
  try {
    const res = await fetchImpl(
      PORTAL_CLAIM_REDEEM_PATH,
      jsonInit({
        tenant: input.tenant,
        code: input.code,
        password: input.password,
      }),
    );
    return classifyClaimHttp(res.status, res.headers.get('content-type') || '', await res.text());
  } catch {
    return { status: 'unavailable' };
  }
}

export async function rerequestPortalClaim(
  input: { tenant: string; code: string },
  fetchImpl: ClaimFetch = fetch,
): Promise<ClaimCallResult> {
  if (!TENANT_RE.test(input.tenant) || !CODE_RE.test(input.code)) {
    return { status: 'incomplete' };
  }
  try {
    const res = await fetchImpl(
      PORTAL_CLAIM_REREQUEST_PATH,
      jsonInit({
        tenant: input.tenant,
        code: input.code,
        reason: 'rerequest',
      }),
    );
    return classifyClaimRerequestHttp(
      res.status,
      res.headers.get('content-type') || '',
      await res.text(),
    );
  } catch {
    return { status: 'unavailable' };
  }
}

export function claimNotice(status: ClaimCallStatus, kind: 'redeem' | 'rerequest'): string {
  if (kind === 'rerequest' && status === 'success') return PORTAL_CLAIM_TEXT.rerequestDone;
  if (kind === 'rerequest' && status !== 'success') return PORTAL_CLAIM_TEXT.rerequestFail;
  switch (status) {
    case 'success':
      return PORTAL_CLAIM_TEXT.success;
    case 'too_short':
      return PORTAL_CLAIM_TEXT.too_short;
    case 'mismatch':
      return PORTAL_CLAIM_TEXT.mismatch;
    case 'used':
      return PORTAL_CLAIM_TEXT.used;
    case 'expired':
      return PORTAL_CLAIM_TEXT.expired;
    case 'invalid':
      return PORTAL_CLAIM_TEXT.invalid;
    case 'incomplete':
      return PORTAL_CLAIM_TEXT.incomplete;
    default:
      return PORTAL_CLAIM_TEXT.unavailable;
  }
}
